const FORM_SELECTOR = '.form-block .EPiServerForms';
const STATUS_MESSAGE_CLASS = 'Form__Status__Message';
const SUCCESS_MESSAGE_CLASS = 'Form__Success__Message';
const FORM_ELEMENT_CLASS = 'Form__Element';
const VALIDATION_ERROR_CLASS = 'Form__Element__ValidationError';
const HIDDEN_CLASS = 'hidden';
const FORM_EVENT_TYPE = {
    COMPLETE: 'web.formFilledOut.complete',
    ERROR: 'web.formFilledOut.error',
};


export const generateUUID = () => {
    return 'xxxxxxxx-xxxx-xxxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        // eslint-disable-next-line no-bitwise
        const r = Math.random() * 16 | 0;
        // eslint-disable-next-line no-bitwise, no-mixed-operators
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

const hadClass = (oldValue, className) => {
    return (oldValue ?? '').split(/\s+/).includes(className);
};

// returns true only when the class has just been added (not already present before the mutation)
const classAdded = (mutation, className) => {
    return mutation.target.classList.contains(className) && !hadClass(mutation.oldValue, className);
};

const getFormInfo = form => {
    const formGuid = form.querySelector('input[name="__FormGuid"]')?.value ?? '';
    const formName = form.querySelector('.Form__Title')?.textContent.trim() ?? '';

    return { formGuid, formName };
};

const getFieldName = element => {
    return element.querySelector('.Form__Element__Caption')?.textContent.trim()
        || element.dataset.fElementName
        || '';
};

// text mutations target the span's child nodes, so walk up to the validation error span itself
const getValidationErrorSpan = node => {
    const element = node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    return element?.closest(`.${VALIDATION_ERROR_CLASS}`) ?? null;
};

const getVisibleError = span => {
    if (span.classList.contains(HIDDEN_CLASS)) {
        return '';
    }

    return span.textContent.trim();
};

const addDigitalDataForm = (dataToAdd, eventToDispatch) => {
    window.digitalData = {
        ...(window.digitalData ? window.digitalData : {}),
        ...dataToAdd,
    };
    if (eventToDispatch && typeof eventToDispatch === 'string') {
        window.dispatchEvent(new CustomEvent(eventToDispatch));
    }
};

const adobeAnalyticsFormSuccess = form => {
    const formInfo = getFormInfo(form);
    const dataToAdd = {
        form: {
            fieldInfo: {
                eventID: generateUUID(),
                eventType: FORM_EVENT_TYPE.COMPLETE,
            },
            formname: formInfo.formName,
        },
    };
    addDigitalDataForm(dataToAdd, FORM_EVENT_TYPE.COMPLETE);
};

const adobeAnalyticsFormError = (form, failedFields) => {
    if (!failedFields || failedFields.length < 1) {
        return;
    }

    const formInfo = getFormInfo(form);
    const errorFields = failedFields.map(field => {
        return field.field ?? '';
    })
        .filter(field => {
            return !!field;
        })
        .join(',');

    const errorMessages = failedFields.map(field => {
        const errField = field.field ?? '';
        const errMsg = field.error ?? '';
        return `${errField}: ${errMsg}`;
    })
        .join(',');

    const dataToAdd = {
        form: {
            fieldInfo: {
                eventID: generateUUID(),
                eventType: FORM_EVENT_TYPE.ERROR,
                errorMessage: errorMessages,
                fieldName: errorFields,

            },
            formname: formInfo.formName,
        },
    };
    addDigitalDataForm(dataToAdd, FORM_EVENT_TYPE.ERROR);
};

const adobeFormAnalytics = {
    observeForm: form => {
        // last error reported for each span, so the same error isn't reported twice in a row
        const reportedErrors = new WeakMap();

        const observer = new MutationObserver(mutations => {
            const changedSpans = new Set();
            let isSuccess = false;

            mutations.forEach(mutation => {
                const { target } = mutation;

                if (mutation.type === 'attributes'
                    && target.classList.contains(STATUS_MESSAGE_CLASS)
                    && classAdded(mutation, SUCCESS_MESSAGE_CLASS)) {
                    isSuccess = true;
                }

                const span = getValidationErrorSpan(target);
                if (span) {
                    changedSpans.add(span);
                }
            });

            const failedFields = [];

            changedSpans.forEach(span => {
                const error = getVisibleError(span);

                if (error && error !== reportedErrors.get(span)) {
                    failedFields.push({
                        field: getFieldName(span.closest(`.${FORM_ELEMENT_CLASS}`) ?? span),
                        error,
                    });
                }

                // reset once hidden or emptied, so the same error can be reported again on the next failure
                reportedErrors.set(span, error);
            });

            if (isSuccess) {
                adobeAnalyticsFormSuccess(form);
            }

            // a single validation pass can fail several fields at once, so report them as one error event
            if (failedFields.length) {
                adobeAnalyticsFormError(form, failedFields);
            }
        });

        observer.observe(form, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['class'],
            attributeOldValue: true,
        });
    },

    init: () => {
        document.querySelectorAll(FORM_SELECTOR).forEach(form => {
            console.info('Observing form:', form);
            adobeFormAnalytics.observeForm(form);
        });
    },
};

document.addEventListener('DOMContentLoaded', () => {
    adobeFormAnalytics.init();
});
